import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './estilodashuser.css';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('pt-BR');
}

function getStatusNumber(value) {
  return Number(value) === 1 ? 1 : 0;
}

function getStatusLabel(value) {
  return getStatusNumber(value) === 1 ? 'Ativa' : 'Inativa';
}

function getTipoLabel(tipo) {
  const t = String(tipo || '').toLowerCase();
  if (t === 'real') return 'Real';
  if (t === 'demo') return 'Demo';
  return tipo || '—';
}

function getProdutoDisplay(licenca) {
  const raw = String(
    licenca?.produto_nome ||
    licenca?.nome_produto ||
    licenca?.produto_codigo ||
    licenca?.codigo_plano ||
    ''
  ).trim();

  const normalized = raw.toLowerCase();

  if (
    normalized === 'ea' ||
    normalized.includes('quantum trades ea') ||
    normalized.includes('quantumtrades ea') ||
    normalized.includes('quantum trades ia') ||
    normalized.includes('quantumtrades ia')
  ) {
    return 'Quantum Trades IA';
  }

  if (
    normalized === 'bias' ||
    normalized.includes('quantumtrades bias') ||
    normalized.includes('quantum trades bias')
  ) {
    return 'Quantum Trades Bias';
  }

  if (
    normalized === 'ragnarok' ||
    normalized.includes('ragnarok') ||
    normalized.includes('ragnarock')
  ) {
    return 'QuantumTrades Ragnarok';
  }

  return raw || 'Produto';
}

export default function ListarLicencas({ userId }) {
  const [licencas, setLicencas] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [removendoId, setRemovendoId] = useState('');

  useEffect(() => {
    if (userId) {
      carregarDados();
    }
  }, [userId]);

  const carregarDados = async () => {
    try {
      setCarregando(true);
      setMensagem('');
      setTipoMensagem('');

      const [respLicencas, respAssinaturas] = await Promise.all([
        api.get('/api/minhas-licencas'),
        api.get('/api/minhas-assinaturas'),
      ]);

      setLicencas(respLicencas.data || []);
      setAssinaturas(respAssinaturas.data || []);
    } catch (error) {
      console.error('Erro ao carregar licenças do cliente:', error);
      setMensagem('Erro ao carregar suas licenças. Tente novamente em alguns instantes.');
      setTipoMensagem('erro');
    } finally {
      setCarregando(false);
    }
  };

  const resumo = useMemo(() => {
    const licencasAtivas = licencas.filter((l) => getStatusNumber(l.status) === 1);
    const licencasDemo = licencas.filter(
      (l) => String(l.tipo || '').toLowerCase() === 'demo'
    ).length;
    const licencasReal = licencas.filter(
      (l) => String(l.tipo || '').toLowerCase() === 'real'
    ).length;

    const assinaturasAtivas = assinaturas.filter((a) => Number(a.status) === 1);

    const slotsTotais = assinaturasAtivas.reduce(
      (acc, item) => acc + Number(item.slots_contratados || 0),
      0
    );

    const slotsDisponiveis = Math.max(slotsTotais - licencasAtivas.length, 0);

    return {
      totalLicencas: licencas.length,
      licencasAtivas: licencasAtivas.length,
      licencasDemo,
      licencasReal,
      slotsTotais,
      slotsDisponiveis,
    };
  }, [licencas, assinaturas]);

  const resumoAssinaturas = useMemo(() => {
    const ativas = assinaturas.filter((a) => Number(a.status) === 1);

    if (!ativas.length) {
      return 'Nenhuma assinatura ativa encontrada no momento.';
    }

    return ativas
      .map((a) => {
        const nome = a.nome_plano || a.codigo_plano || 'Assinatura';
        return `${nome} (${a.tier})`;
      })
      .join(' • ');
  }, [assinaturas]);

  const removerLicenca = async (licenca) => {
    const conta = licenca?.conta || 'esta conta';
    const confirmou = window.confirm(
      `Remover a licença da conta MT5 ${conta}? Depois você poderá cadastrar uma nova conta em Adicionar Licença.`
    );

    if (!confirmou) return;

    try {
      setRemovendoId(String(licenca.id));
      setMensagem('');
      setTipoMensagem('');

      await api.delete(`/api/minhas-licencas/${licenca.id}`);
      setLicencas((atuais) =>
        atuais.filter((item) => String(item.id) !== String(licenca.id))
      );
      setMensagem('Licença removida com sucesso. Agora você pode cadastrar outra conta.');
      setTipoMensagem('sucesso');
    } catch (error) {
      console.error('Erro ao remover licença:', error);
      setMensagem(
        error.response?.data?.message ||
          'Erro ao remover a licença. Tente novamente em alguns instantes.'
      );
      setTipoMensagem('erro');
    } finally {
      setRemovendoId('');
    }
  };

  return (
    <div className="lic-page">
      <div className="lic-card">
        <div className="lic-header">
          <div className="lic-title">
            <span className="lic-ico">📜</span>
            <div>
              <h3 className="lic-h3">Minhas Licenças</h3>
              <div className="lic-subtitle">
                Aqui você acompanha as contas MT5 cadastradas, o tipo da licença e o status de cada uma.
              </div>
            </div>
          </div>

          <button type="button" className="lic-btn-outline qt-btn qt-btn-secondary" onClick={carregarDados}>
            Recarregar
          </button>
        </div>

        <div className="cliente-help-box" style={{ marginBottom: 14 }}>
          <strong>Orientação:</strong> confira se a conta MT5 cadastrada está correta e se o status da licença está ativo.
          Caso precise trocar uma conta, remova a licença atual nesta lista e depois cadastre a nova conta em <strong>Adicionar Licença</strong>.
          Se houver divergência entre contratação e licenças cadastradas, fale com o suporte.
        </div>

        <div className="lic-kpis">
          <div className="lic-kpi">
            <div className="lic-kpi-label">Licenças cadastradas</div>
            <div className="lic-kpi-value">{resumo.totalLicencas}</div>
          </div>

          <div className="lic-kpi">
            <div className="lic-kpi-label">Licenças ativas</div>
            <div className="lic-kpi-value">{resumo.licencasAtivas}</div>
          </div>

          <div className="lic-kpi">
            <div className="lic-kpi-label">Licenças Real</div>
            <div className="lic-kpi-value">{resumo.licencasReal}</div>
          </div>

          <div className="lic-kpi">
            <div className="lic-kpi-label">Licenças Demo</div>
            <div className="lic-kpi-value">{resumo.licencasDemo}</div>
          </div>
        </div>

        <div className="summary-panel">
          <div className="summary-panel-title">Resumo da contratação</div>
          <div className="summary-panel-main">{resumoAssinaturas}</div>
          <div className="summary-panel-sub">
            Slots liberados pelas assinaturas ativas: <strong>{resumo.slotsTotais}</strong> •
            Disponíveis no momento: <strong>{resumo.slotsDisponiveis}</strong>
          </div>
        </div>

        {mensagem && (
          <div
            className={
              tipoMensagem === 'erro'
                ? 'alert-box alert-error'
                : 'alert-box alert-success'
            }
          >
            {mensagem}
          </div>
        )}

        {carregando && <p className="lic-loading">Carregando suas licenças...</p>}

        <div className="lic-table-wrap">
          <table className="lic-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th>Conta MT5</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Criada em</th>
                <th>Expira em</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {!carregando && licencas.length === 0 ? (
                <tr>
                  <td colSpan="7">Nenhuma licença cadastrada até o momento.</td>
                </tr>
              ) : (
                licencas.map((licenca) => (
                  <tr key={licenca.id}>
                    <td>
                      <div className="lic-produto-nome">
                        {getProdutoDisplay(licenca)}
                      </div>
                    </td>

                    <td className="lic-mono">{licenca.conta || '—'}</td>
                    <td>{getTipoLabel(licenca.tipo)}</td>
                    <td>
                      <span
                        className={
                          getStatusNumber(licenca.status) === 1
                            ? 'qt-badge qt-badge-success'
                            : 'qt-badge qt-badge-danger'
                        }
                      >
                        {getStatusLabel(licenca.status)}
                      </span>
                    </td>

                    <td>{formatDate(licenca.created_on)}</td>
                    <td>{formatDate(licenca.date_exp)}</td>
                    <td>
                      <div className="lic-actions">
                        <button
                          type="button"
                          className="lic-btn-danger qt-btn"
                          onClick={() => removerLicenca(licenca)}
                          disabled={removendoId === String(licenca.id)}
                          title="Remover esta licença para cadastrar outra conta MT5"
                        >
                          {removendoId === String(licenca.id) ? 'Removendo...' : 'Remover'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lic-footnote">
          Licenças <strong>Demo</strong> são úteis para testes. Licenças <strong>Real</strong> dependem da contratação e da situação de pagamento para validação no robô.
        </div>
      </div>
    </div>
  );
}
